import React, { useRef } from 'react';
import Immutable from 'immutable';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  Container,
  Image,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  Paragraph,
  Taxonomy,
  View,
} from '@uncinc/react-drupal-core';

import {
  Share,
  GetStarted,
  WysiwygField,
  CampaignBlock,
} from '../../../components';


import {
  SCREEN_MOBILE,
  SCREEN_NORMAL,
  SCREEN_TABLET,
} from '../../../config/constants';
import NodeMetadata from '../../../components/NodeMetadata';
import Faq from '../../../components/Molecules/Faq';
import MemberWall from '../../../components/MemberWall';
import NewsletterBlock from '../../../components/NewsletterBlock';
import './style.scss';
import Tags from '../../../components/Tags';
import ExpertsSlider from '../../../components/ExpertsSlider';
import useLoggedInStatus from '../../../hooks/useLoggedInstatus';

const NodePracticalExamplePage = ({ entity, className }) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useLoggedInStatus();
  const classes = classNames(className, 'NodePracticalExample');
  const theme = entity.getIn(['field_theme', 0, 'entity']);
  const created = new Date(Date.parse(entity.getIn(['created', 0, 'value'])));
  const title = entity.getIn(['title', 0, 'value'], '');
  const intro = entity.getIn(['field_intro', 0, 'processed']);
  const body = entity.getIn(['body', 0, 'processed']);
  const type = entity.getIn(['field_practicalexample_type', 0, 'entity', 'name', 0, 'value'], null);
  const paragraphs = entity.getIn(['field_paragraphs'], Immutable.Map());
  const experts = entity.getIn(['field_experts'], Immutable.Map());
  const subtitle = entity.getIn(['field_subtitle', 0, 'value']);

  const getStartedRef = useRef();
  const getStartedContent = entity.getIn(['field_get_started_content'], Immutable.Map()).concat(entity.getIn(['field_get_started_tax_content']));
  const subjects = entity.getIn(['field_subject']);
  const workfields = entity.getIn(['field_workfield']);
  const relatedDossierArgs = entity.getIn(['field_subject'], Immutable.List())
    .concat(entity.getIn(['field_workfield'], Immutable.List()));
  const expertsTitle = entity.getIn(['field_experts_title', 0, 'value'], t('expert.node_title'));
  const campaign = entity.getIn(['field_campaign', 0, 'entity'], null);
  const isMemberOnlyContent = entity.getIn(['field_member_only_content', 0, 'value'], false);

  const showMemberWall = !isLoggedIn && isMemberOnlyContent;

  return (
    <article typeof="schema:WebPage" className={classes}>

      <Container>
        {entity.getIn(['field_media_image', 0]) && (
          <div className="NodePracticalExample__hero">
            <Image
              lazyload={false}
              className="NodePracticalExample__hero__image"
              includeWebP
              src={{
                [SCREEN_MOBILE]: entity.getIn(['field_media_image', 0, 'styles', 'hero_mobile']),
                [SCREEN_TABLET]: entity.getIn(['field_media_image', 0, 'styles', 'hero_tablet']),
                [SCREEN_NORMAL]: entity.getIn(['field_media_image', 0, 'styles', 'hero_desktop']),
              }}
              alt={entity.getIn(['field_media_image', 0, 'alt'], '')}
            />
          </div>
        )}
        <div className="NodePracticalExample__content__wrapper row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="NodePracticalExample__content">
              <NodeMetadata
                icon="icon-copy"
                displayType={type}
                created={created}
                theme={theme}
              />
              <h1>{title}</h1>

              {subtitle && (
                <div className="NodePracticalExample__subtitle">
                  {subtitle}
                </div>
              )}

              {intro && (
                <WysiwygField
                  property="schema:text"
                  className="NodePracticalExample__intro subheading2"
                  html={intro}
                />
              )}

              {body && (
                <WysiwygField
                  property="schema:text"
                  className="NodePracticalExample__body subheading2"
                  html={body}
                />
              )}

              <div className="Accordion">
                {entity.getIn(['field_aanleiding', 0, 'processed']) && (
                  <Faq question="Aanleiding" answer={entity.getIn(['field_aanleiding', 0, 'processed'])} />
                )}
                {entity.getIn(['field_approach', 0, 'processed']) && (
                  <Faq question="Aanpak" answer={entity.getIn(['field_approach', 0, 'processed'])} />
                )}
                {entity.getIn(['field_results', 0, 'processed']) && (
                  <Faq question="Resultaat" answer={entity.getIn(['field_results', 0, 'processed'])} />
                )}
                {entity.getIn(['field_inzichten', 0, 'processed']) && (
                  <Faq question="Inzichten" answer={entity.getIn(['field_inzichten', 0, 'processed'])} />
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>

      <div className="NodeContentWrapper">
        {paragraphs.size > 0 && (
          <div className="Paragraphs">
            {
              paragraphs.map((item, index) => {
                if (showMemberWall && index > 0) return;
                const paragraphId = item.get('target_id');
                const paragraph = item.getIn(['entity']);

                return (
                  <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                );
              })
            }
          </div>
        )}

        <ExpertsSlider
          expertsTitle={expertsTitle}
          experts={experts}
        />

        {campaign && (
          <CampaignBlock entity={campaign} />
        )}

        <GetStarted
          title={entity.getIn(['field_get_started_title', 0, 'value'])}
          subtitle={entity.getIn(['field_get_started_subtitle', 0, 'value'])}
          content={getStartedContent}
        />

        {relatedDossierArgs && relatedDossierArgs.size > 0 && (
          <div className="TaxonomySubject__related_dossiers">
            <Container>
              <h2>{entity.getIn(['field_related_dossiers_title', 0, 'value'], 'Dit artikel is onderdeel van')}</h2>
              <View id="article_dossiers_related" display="block_1" args={relatedDossierArgs} />
            </Container>
          </div>
        )}

        {entity.getIn(['field_paragraph_feedback', 0]) && (
          <div className="NodeArticleDetail__feedback">
            {
              entity.getIn(['field_paragraph_feedback']).map((item, index) => {
                const paragraphId = item.get('target_id');
                const paragraph = item.getIn(['entity']);

                return (
                  <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                );
              })
            }
          </div>
        )}

        <Tags subjects={subjects} workfields={workfields} />
        {theme && (
          <Container>
            <div className="row">
              <div className='col-12'>
                <Taxonomy
                  id={theme.getIn(['tid', 0, 'value'])}
                  entity={theme}
                  viewMode='card'
                />
              </div>
            </div>
          </Container>
        )}
        <Container>
          <div className="row">
            <div className="col-12 col-lg-8 offset-lg-2">
              <Share pageTitle={entity.getIn(['title', 0, 'value'], '')} />
            </div>
          </div>
        </Container>
      </div>


      {/* Member wall. */}
      {showMemberWall && (
        <MemberWall />
      )}

      {/* Newsletter subscription block */}
      <NewsletterBlock />
    </article>
  );
};

export default NodePracticalExamplePage;
